import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import NumberField from "@/components/NumberField";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import {
  ArrowRight,
  CarProfile,
  Plus,
  PencilSimple,
  Trash,
  Wrench,
  Note,
  X as XIcon,
  IdentificationCard,
} from "@phosphor-icons/react";

const emptyForm = () => ({
  plate: "",
  consumables_expenses: 0,
  notes: "",
});

/* ────────────────────────  Add / Edit Modal  ──────────────────────── */
function VehicleModal({ open, initial, carTypeId, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        plate: initial.plate || "",
        consumables_expenses: initial.consumables_expenses || 0,
        notes: initial.notes || "",
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.plate.trim()) return toast.error("رقم اللوحة مطلوب");
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/fleet/${encodeURIComponent(initial.plate)}`, {
          plate: form.plate.trim(),
          car_type: carTypeId,
          consumables_expenses: Number(form.consumables_expenses || 0),
          notes: form.notes.trim(),
        });
        toast.success("تم حفظ التعديلات");
      } else {
        await api.post("/admin/fleet", {
          plate: form.plate.trim(),
          car_type: carTypeId,
          consumables_expenses: Number(form.consumables_expenses || 0),
          notes: form.notes.trim(),
        });
        toast.success("تم إضافة السيارة");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="fleet-vehicle-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-[#0F1F42] border border-primary/30 rounded-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between p-4 border-b border-primary/20">
          <h3 className="text-white font-bold flex items-center gap-2">
            <CarProfile size={18} weight="duotone" className="text-primary" />
            {isEdit ? "تعديل السيارة" : "إضافة سيارة جديدة"}
          </h3>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white">
            <XIcon size={18} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-white/60 mb-1 flex items-center gap-1">
              <IdentificationCard size={12} className="text-primary/70" /> رقم اللوحة *
            </label>
            <input
              type="text"
              value={form.plate}
              onChange={(e) => set("plate", e.target.value)}
              className="mz-input w-full text-right"
              placeholder="مثال: أ ب ج 1234"
              data-testid="fleet-plate-input"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1 flex items-center gap-1">
              <Wrench size={12} className="text-[#F87171]" /> المصاريف الاستهلاكية (ر.س)
            </label>
            <NumberField
              value={form.consumables_expenses}
              onChange={(v) => set("consumables_expenses", v)}
              step="0.01"
              min={0}
              testId="fleet-consumables-input"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1 flex items-center gap-1">
              <Note size={12} /> ملاحظات (اختياري)
            </label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="mz-input w-full text-right"
              placeholder="أي ملاحظات عن السيارة"
              data-testid="fleet-notes-input"
            />
          </div>
        </div>
        <div className="p-4 border-t border-primary/20 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-white/20 text-white/80 hover:bg-white/5 text-sm"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="fleet-save-btn"
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:brightness-110 disabled:opacity-50"
          >
            {saving ? "جارٍ الحفظ…" : isEdit ? "حفظ" : "إضافة"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ────────────────────────  Main Page  ──────────────────────── */
export default function FleetVehicles() {
  const { carType } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [carNameAr, setCarNameAr] = useState(carType);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fleet", { params: { car_type: carType } });
      setItems(data.items || []);
      if (data.items?.length > 0) setCarNameAr(data.items[0].car_name_ar || carType);
      else {
        // Look up the name from /cars if no items exist yet
        try {
          const carsRes = await api.get("/cars");
          const match = (carsRes.data || []).find((c) => c.id === carType);
          if (match) setCarNameAr(match.name_ar || carType);
        } catch { /* silent */ }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [carType]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (v) => {
    const ok = await confirm({
      title: "حذف السيارة",
      message: `سيتم حذف اللوحة "${v.plate}" نهائياً. الحجوزات المرتبطة بها لن تتأثر.`,
      confirmText: "حذف نهائي",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/fleet/${encodeURIComponent(v.plate)}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              data-testid="fleet-back-btn"
              className="w-10 h-10 rounded-xl border border-white/15 text-white/70 hover:text-white hover:bg-white/5 flex items-center justify-center"
              title="رجوع"
            >
              <ArrowRight size={18} weight="bold" />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">{carNameAr}</h1>
              <p className="text-white/50 text-sm mt-0.5">
                السيارات المسجّلة (<span className="num">{fmtNum(items.length)}</span>)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setEditing(null); setModalOpen(true); }}
            data-testid="fleet-add-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:brightness-110"
          >
            <Plus size={18} weight="bold" /> إضافة سيارة
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 py-16 text-center" data-testid="fleet-empty">
            <CarProfile size={56} weight="duotone" className="text-primary/50 mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-1">لا توجد سيارات مسجّلة بعد</h3>
            <p className="text-white/50 text-sm">اضغط زر <span className="text-primary font-bold">➕ إضافة سيارة</span> في الأعلى لإضافة أول سيارة تحت هذا النوع</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((v) => (
              <div
                key={v.plate}
                data-testid={`fleet-card-${v.plate}`}
                className="fadeup rounded-2xl border border-primary/20 bg-card hover:border-primary/50 transition-all overflow-hidden"
              >
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/20 border border-primary/50 text-primary font-bold text-sm num shrink-0"
                      dir="ltr"
                      data-testid={`fleet-plate-${v.plate}`}
                    >
                      <CarProfile size={14} weight="duotone" /> {v.plate}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setEditing(v); setModalOpen(true); }}
                        title="تعديل"
                        data-testid={`fleet-edit-${v.plate}`}
                        className="w-8 h-8 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 flex items-center justify-center"
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => handleDelete(v)}
                        title="حذف"
                        data-testid={`fleet-delete-${v.plate}`}
                        className="w-8 h-8 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10 flex items-center justify-center"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Wrench size={14} weight="fill" className="text-[#F87171]" />
                    <span className="text-white/70">المصاريف الاستهلاكية:</span>
                    <span className="text-[#F87171] font-bold num" dir="ltr">
                      {fmtRiyal(v.consumables_expenses || 0)}
                    </span>
                  </div>
                  {v.notes && (
                    <div className="flex items-start gap-2 text-xs pt-2 border-t border-primary/10">
                      <Note size={12} className="text-white/40 mt-0.5 shrink-0" />
                      <p className="text-white/50 italic leading-relaxed break-words">{v.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <VehicleModal
        open={modalOpen}
        initial={editing}
        carTypeId={carType}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={load}
      />
    </div>
  );
}
